import { Dayjs } from 'dayjs';
import { BookingDetailsAdditionalService } from '../store/slices/bookingCreationSlice';
import { BookingStatusType } from '../components/StatusBadge';
import { PricingDiscount } from '../api/types';

export type DashboardDateRangeType = 'last7days' | 'currentMonth' | 'lastMonth' | 'yearToDate' | 'customRange';

export interface DashboardDateRange {
  from: string;
  to: string;
}

export interface AssetAvailabilityDateRange {
  year: string;
  month: string;
}

export interface AdditionalService {
    [x: string]: any;
    id: string;
    assetId: string;
    serviceName: string;
    langNames?: Record<string, string>;
    unitPrice: number;
    isPaymentCompulsory: boolean;
    quantity: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface SearchRoomDataValidation {
    city: boolean;
    checkInDate: boolean;
    checkOutDate: boolean;
    numberOfGuests: boolean;
}
export interface BookingDetailsSearchRoomData {
    city: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    numberOfGuests: number;
}

export interface ApartmentDetails {
    id: string;
    state: string;
    name: string;
    groupId: string;
    depotId: string;
    description: string | null;
    usedCounter: number;
    lastRentalId: string | null;
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
    additionalImages: string[];
    accomodationdetail: {
        guests: number;
        bedrooms: number;
        baths: number;
        beds: number;
        policies: Array<{
            html?: string;
            icon: string;
            title: string;
            description: string;
        }>;
        amenities: Array<{
            key: string;
            icon: string;
            type: string;
            value: string | number;
        }>;
        importantNote: string;
        facilityDescription: string;
    };
    Group: {
        location: {
            id?: string;
            name?: string;
            addressId?: string;
            statusId?: string;
            services?: string[];
            uniqueCode?: string;
            archivedAt?: string | null;
            createdAt?: string;
            updatedAt?: string;
            locationImages?: string[];
            address: {
                id: string;
                street: string;
                city: string;
                postalCode: string;
                geoLat: number;
                geoLng: number;
                archivedAt: string | null;
                createdAt: string;
                updatedAt: string;
            };
        };
        pricecategory?: {
            pricescale: Array<{
                id: string;
                priceCategoryId: string;
                durationInMinutes: number;
                isFullDay: boolean | null;
                price: number;
                archivedAt: string | null;
                createdAt: string;
                updatedAt: string;
            }>;
        };
    };
    assetAdditionalServices?: {
        id: string;
        assetId: string;
        serviceName: string;
        langNames?: Record<string, string>;
        unitPrice: number;
        isPaymentCompulsory: boolean;
        quantity: boolean;
        createdAt: string;
        updatedAt: string;
    }[];
    pricingDiscounts: PricingDiscount[];
}
export interface CreateRoomRentalBookingPayload {
    checkIn: Dayjs;
    checkOut: Dayjs;
    startTime?: string;
    endTime?: string;
    city: string;
    numberOfGuests: number;
    apartmentId: string;
    /** #2650: the offer being booked. Optional — absent means the object's
     *  own group, which is every caller except the venue pages. */
    groupId?: string;
    /**
     * #1808: the customer class this booking is priced under. Optional —
     * absent means no override, and the backend resolves account ->
     * organization -> the tenant's system default. It is NOT the same thing
     * as "the general rate": that is what a tariff ROW with no class means.
     */
    customerClassId?: string | null;
    /**
     * #1810: who the booking is made for, and the invoice recipient's cost
     * centre. Sent only by operational tenants (legacy stores refuse them).
     * `costCenter` null = none; the form always sends what it shows.
     */
    organizationId?: string | null;
    organizationUnitId?: string | null;
    costCenter?: string | null;
    locationId: string;
    additionalServices: {
        serviceId: string;
        name: string;
        quantity: number;
    }[];
    customerDetails: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: string;
        country: string;
        city: string;
        zipCode: string;
        company: string;
    };
    someoneElseDetails: {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
    };
    useCase: string;
    discount: number;
    // #1942: a price adjustment made at creation is persisted on the
    // reservation with the same audit trail an edit writes — the reason the
    // admin typed. Only sent when there is an adjustment; who applied it comes
    // from the verified token server-side (#3261).
    priceAdjustmentReason?: string;
    comment: string;
    emailNote?: string;
    eventType?: string | null;
    isFullyDiscounted?: boolean;
    seriesId?: string | null;
    isFakeForBlocking?: boolean;
    reasonForBlocking?: string;
}

export interface EditRoomRentalBookingPayload {
    checkIn: Dayjs;
    checkOut: Dayjs;
    city: string;
    invoiceNumber?: string;
    numberOfGuests: number;
    apartmentId: string;
    /** #2650: the offer being booked. Optional — absent means the object's
     *  own group, which is every caller except the venue pages. */
    groupId?: string;
    /**
     * #1810: who the booking is made for, and the invoice recipient's cost
     * centre. Sent only by operational tenants (legacy stores refuse them).
     * `costCenter` null = none; the form always sends what it shows.
     */
    organizationId?: string | null;
    organizationUnitId?: string | null;
    costCenter?: string | null;
    /**
     * #1808: the class override (null = none, the chain decides). Sent only
     * once the form was seeded from the booking's stored override; the server
     * reprices when it differs from the stored one, and ignores it otherwise.
     */
    customerClassId?: string | null;
    locationId: string;
    additionalServices: {
        serviceId: string;
        name: string;
        quantity: number;
    }[];
    customerDetails: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: string;
        country: string;
        city: string;
        zipCode: string;
        company: string;
    };
    someoneElseDetails: {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
    };
    useCase: string;
    paymentStatus?: 'paid' | 'unpaid' | 'partially_paid' | 'overpaid';
    discount: number;
    // #1000 Step 7C: manual final-price override (SSoT re-bill). When set, the
    // final price becomes the billed total (replacing `discount`); the acting
    // admin comes from the verified token server-side (#3261).
    manualFinalPrice?: number;
    priceAdjustmentReason?: string;
    /** Clear a previously-stored override (adjustment set back to 0). */
    clearPriceOverride?: boolean;
    alreadyPaid: number;
    comment: string;
    emailNote?: string;
    eventType?: string | null;
}

export type SeriesPaymentMode = 'lump_sum' | 'instalments';
export type SeriesPaymentCadence = 'monthly' | 'quarterly' | 'custom';
export type SeriesPaymentTiming = 'upfront' | 'end';

// Structured payment-term shape persisted on the series. See PRs #974-#980
// for the migration away from the old flat `paymentFrequency` flag.
export interface SeriesPaymentTerm {
    paymentMode: SeriesPaymentMode;
    paymentCadence: SeriesPaymentCadence | null;
    paymentCount: number | null;
    paymentTiming: SeriesPaymentTiming | null;
}

// Phase-2 fields (customer/discount/services) are optional because the
// same endpoint is also used by the series-creation finalize step in
// BatchCreationProgress, which only commits the payment term. Backend
// (still Phase-1) ignores the optional fields today; this keeps the
// type forward-compatible once the Phase-2 backend (#770/#771) ships.
/**
 * The body of the series preview and create routes (new-backend
 * `lib/series/seriesRequest.ts`). The booking fields keep the single-booking
 * form's names.
 */
export interface SeriesRequestPayload {
    pattern: import('../utils/recurrence').ServerSeriesPattern;
    startTime: string;
    endTime: string;
    groupId: string;
    customerDetails: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: string;
        country: string;
        city: string;
        zipCode: string;
        company: string;
    };
    numberOfGuests: number;
    eventType: string | null;
    comment: string;
    additionalServices: { serviceId: string, quantity: number }[];
    /** #1810: see `attributionPayload`. */
    organizationId?: string | null;
    organizationUnitId?: string | null;
    costCenter?: string | null;
}

/** Create: the request, the dates removed after the preview, the term. */
export interface CreateSeriesPayload extends SeriesRequestPayload, Partial<SeriesPaymentTerm> {
    excludedDates: string[];
    /**
     * Taken dates to create anyway, as conflicts (admin only). Every other
     * date stays under the booking guard.
     */
    forcedDates?: string[];
    /**
     * Public holidays to book anyway (M9, admin only): the server leaves them
     * out unless named here.
     */
    includedHolidays?: string[];
    /** Add the dates to this existing series instead of starting a new one. */
    seriesId?: string;
}

export interface CreateSeriesResult {
    date: string;
    outcome: 'created' | 'skipped' | 'failed';
    orderId?: string;
    bookingNumber?: string;
    reason?: string;
    /** Created despite the guard, as a conflict (a `forcedDates` date). */
    conflict?: boolean;
}

export interface CreateSeriesResponse {
    seriesId: string | null;
    results: CreateSeriesResult[];
    mailSent: boolean;
}

export interface EditSeriesBookingPayload extends SeriesPaymentTerm {
    /** #1810: applied to every alive occurrence alike. */
    organizationId?: string | null;
    organizationUnitId?: string | null;
    costCenter?: string | null;
    customerDetails?: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: string;
        country: string;
        city: string;
        zipCode: string;
        company: string;
    };
    // Series-level discount in EUR, applied on top of per-booking discounts
    // (volume-discount semantics). See issue #771.
    discount?: number;
    // Per-occurrence add-ons — the same list applies to every booking in the
    // series with the given quantity per occurrence. See issue #771.
    additionalServices?: {
        serviceId: string;
        name: string;
        quantity: number;
    }[];
}

export interface SeriesBookingDetails {
    seriesId: string;
    paymentMode: SeriesPaymentMode;
    paymentCadence: SeriesPaymentCadence | null;
    paymentCount: number | null;
    paymentTiming: SeriesPaymentTiming | null;
    // Series-level discount in EUR (#771). Defaults to 0 when unset.
    discount: number;
    includedBookingCount: number;
    excludedBookingCount: number;
    combinedTotal: number;
    /**
     * Series plan §4.4.1: the series stores the rule it was created from, so
     * "Extend series" can continue it (`POST /admin/series/:seriesId/continuation`).
     * False for series without a rule or with hand-picked dates. Optional:
     * absent on a response from before the field existed.
     */
    canContinue?: boolean;
}

/** `POST /admin/series/:seriesId/continuation`: the next dates of the stored rule. */
export interface SeriesContinuation {
    groupId: string;
    startTime: string;
    endTime: string;
    pattern: import('../utils/recurrence').ServerSeriesPattern;
}

// UI-level selector value. Maps to the three terms the admin can pick;
// the backend's quarterly/custom cadences are intended fast-follows and
// are not yet surfaced in the dropdown.
export type SeriesPaymentTermSelection =
    | 'instalments_monthly'
    | 'lump_sum_upfront'
    | 'lump_sum_end';

export const SERIES_PAYMENT_TERM_SELECTIONS: ReadonlyArray<SeriesPaymentTermSelection> = [
    'instalments_monthly',
    'lump_sum_upfront',
    'lump_sum_end',
];

export const seriesPaymentTermToSelection = (
    s: Pick<SeriesBookingDetails, 'paymentMode' | 'paymentTiming'>,
): SeriesPaymentTermSelection => {
    if (s.paymentMode === 'instalments') return 'instalments_monthly';
    return s.paymentTiming === 'end' ? 'lump_sum_end' : 'lump_sum_upfront';
};

export const selectionToSeriesPaymentTerm = (
    selection: SeriesPaymentTermSelection,
): SeriesPaymentTerm => {
    switch (selection) {
        case 'instalments_monthly':
            return { paymentMode: 'instalments', paymentCadence: 'monthly', paymentCount: null, paymentTiming: null };
        case 'lump_sum_upfront':
            return { paymentMode: 'lump_sum', paymentCadence: null, paymentCount: null, paymentTiming: 'upfront' };
        case 'lump_sum_end':
            return { paymentMode: 'lump_sum', paymentCadence: null, paymentCount: null, paymentTiming: 'end' };
    }
};

export interface SomeoneElse {
    isForSomeoneElse: boolean;
}

export interface SomeoneElseData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

export interface BookingCreationSelection {
    city: string | null;
    checkInDate: Dayjs | null;
    checkOutDate: Dayjs | null;
    startTime: string;
    endTime?: string;
    eventType: string | null;
    amount: number;
    numberOfGuests: number;
    comment: string;
    // Customer-facing note for the confirmation mail — separate from the
    // internal `comment` (never emailed). Transient: sent with the request,
    // not persisted.
    emailNote?: string;
}

export interface PersonalData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    address: string;
    city: string;
    zipCode: string;
    country: string;
    company: string;
}

export interface PersonalDataValidation {
    firstName: boolean;
    lastName: boolean;
    email: boolean;
    phone: boolean;
}

export interface SomeoneElseDataValidation {
    firstName: boolean;
    lastName: boolean;
    email: boolean;
}

export interface Apartment {
    id: string;
    name: string;
    langNames: Record<string, { title: string, description: string }>;
    priceCategoryId: string;
    locationId: string;
    geoLat: number;
    geoLng: number;
    compartmentTypes: string[];
    description: string;
    featuredImage: string;
    maxLimit: number | null;
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
    groupId: string;
    priceCategory: string;
    assetId: string;
    durationInMinutes: number;
    isFullDay: boolean | null;
    price: number;
    priceScales: Array<{
        id: string;
        days?: 'weekday' | 'weekend';
        durationInMinutes: number;
        price: number;
        endTime?: string | null;
        startTime?: string | null;
    }>;
    postalCode?: string | null;
    street?: string | null;
    city?: string | null;
}

export interface BookingCreationValidation {
    orderData: BookingCreationSelection;
    apartment: Apartment | null;
    personalData: PersonalData;
    someoneElseData: SomeoneElseData;
    isForSomeoneElse?: boolean;
}

export interface BookingApprovalDetails {
    state: string | null;
    approvedAt: string | null;
    /** Null when unknown: a master approved, or the approval predates tracking. */
    approvedByName: string | null;
    declinedAt: string | null;
    declinedBy: string | null;
    /** Internal only — never shown to the customer. */
    declineReason: string | null;
}

/** M10 W1-6 (#3042): the fee a cancel under the booking's rule stored. */
export interface BookingCancellationFee {
    amount: number;
    /** Null when the cancel's record is missing. */
    percent: number | null;
    waived: boolean;
}

export interface RentalBookingDetails {
    id: string;
    useCase: string;
    /**
     * #1810: who the booking is made for and the invoice recipient's cost
     * centre. Null (or absent on a legacy tenant, whose store has none of
     * these columns) when not attributed / none.
     */
    organizationId?: string | null;
    organizationName?: string | null;
    organizationUnitId?: string | null;
    organizationUnitName?: string | null;
    costCenter?: string | null;
    /**
     * What the fields above mean: `ok` = stored values; `not_applicable` = a
     * legacy store; `failed` = the read failed and the values are omitted.
     * Absent on an older backend. Edit forms send attribution only on `ok`,
     * so a failed read can never be saved back as "none".
     */
    attributionStatus?: 'ok' | 'not_applicable' | 'failed';
    isFakeForBlocking?: boolean;
    reasonForBlocking?: string | null;
    invoiceNumber?: string;
    bookingNumber: string;
    rentalStartDateTime: string;
    rentalEndDateTime: string;
    created_at: string;
    updated_at: string;
    archivedAt?: string | null;
    archivedBy?: string | null;
    archiveReason?: string | null;
    archiveNote?: string | null;
    anonymizedAt?: string | null;
    status: BookingStatusType;
    payment_status: 'paid' | 'partially_paid' | 'overpaid' | 'unpaid';
    /**
     * The CONTRACT axis (#1701/#1703, postpay) — ISO timestamp once the
     * customer concluded the contract by accepting the offer, else null.
     *
     * Independent of `payment_status`, and deliberately not folded into it: a
     * postpay booking is `booked` and `unpaid` at the same time, legitimately,
     * because it is settled by invoice after use. The payment badge stays the
     * money axis; this is shown beside it so an open amount reads as the claim
     * it is rather than as an oversight.
     *
     * Optional: absent on a legacy tenant, whose database has no such column.
     */
    contractConcludedAt?: string | null;
    /**
     * M8 approval (#1809): the sub-status of a room reservation and who
     * approved or declined it. Absent on a backend older than M8. `state` is
     * read through `toApprovalState`; dates are ISO strings.
     */
    approval?: BookingApprovalDetails;
    /**
     * M10 W1-6 (#3042): the cancellation fee, when the booking was cancelled
     * under its rule; null otherwise, absent on an older backend.
     */
    cancellationFee?: BookingCancellationFee | null;
    /** #1926: an organization carries this booking under its space agreement.
     *  `payment_status` stays 'unpaid' on purpose; read it as covered. Absent
     *  on a legacy tenant. */
    coveredByOrg?: boolean;
    /** The covering organization's display (else legal) name; null when not covered. */
    coveringOrganizationName?: string | null;
    /**
     * PO, 2026-10-23: the list price before any discount is 0 € (a 0 € row, a
     * class at 0 €, "Entgelt kostenfrei"). Read as "Kostenfrei" instead of the
     * payment status, and no invoice is offered. NOT set for a booking a
     * discount brought down to 0 €. Absent on an older backend.
     */
    freeByPrice?: boolean;
    isMailSent: boolean;
    checkIn: Dayjs | null;
    checkOut: Dayjs | null;
    startTime?: string ;
    endTime?: string;
    eventType: string | null;
    setupTime?: string | null;
    lockerLastOpenTime?: string;
    city: string;
    location: string;
    /**
     * #3219: the IANA zone of the booking's location — the clock every day and
     * time of this booking is read and built on (`zoneOfLocation`). Absent on
     * an older backend, which means the station default.
     */
    locationTimeZone?: string;
    comment: string;
    /**
     * The order's `created_by` (`user` = the customer booked in the booking
     * app, `admin` = staff). Decides what `comment` is — the customer's note
     * or the internal one (`commentLabelKey`). Optional: absent on a backend
     * older than this field, which reads as "origin unknown".
     */
    createdBy?: string | null;
    /**
     * #1808: the operator's class override (null = none) and the class the
     * stay was priced under. Null on a legacy tenant; absent on an older
     * backend — the edit form then neither shows nor sends a class.
     */
    customerClassId?: string | null;
    resolvedCustomerClassId?: string | null;
    /**
     * The class "no override" stands for on the edit form: the class a cleared
     * override would price the booking under, by the edit's own resolver. Null
     * = the general price (labelled with the system default).
     */
    noOverrideCustomerClassId?: string | null;
    // Customer-facing note typed on the edit page for the next confirmation
    // mail. Transient (not stored), so it is never pre-filled from the API.
    emailNote?: string;
    address: {
        id: string;
        street: string;
        city: string;
        postalCode: string;
        geoLat: number;
        geoLng: number;
        archivedAt: string | null;
        createdAt: string;
        updatedAt: string;
    };
    numberOfGuests: number;
    additionalService: BookingDetailsAdditionalService[];
    allAdditionalServices?: AdditionalService[];
    apartment: {
      id: string;
      name: string;
    };
    /**
     * Which OFFER this booking is for. Not the same question as
     * `apartment.id` since #1714: a divided room's objects each belong to a
     * SEGMENT, so re-booking from `apartment.id` alone books the segment
     * (#2650). Null on responses older than #2698.
     */
    groupId: string | null;
    customerDetails: {
        // id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        city: string;
        zipCode: string;
        country: string;
        address: string;
        company: string;
    };
    guestDetails: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
    };
    payment: {
        nights: {
            nights: number;
            price: number;
        };
        compulsory?: Array<{
            id: string;
            unitPrice: number;
            serviceName: string;
            langName?: Record<string, string>;
        }>;
        additionalServices?: number;
        total: number;
        amountPaid: number;
        balance: number;
        couponApplied: number;
        // #3332 D2: couponApplied is a prepaid voucher paying part of the
        // price, not a price reduction. Absent on older responses → false.
        paidByPrepaidVoucher?: boolean;
        // #1000 Step 7C: the stored manual override (if any) + the computed
        // price, so the edit screen can prefill the price adjustment.
        manualFinalPrice?: number | null;
        calculatedPrice?: number | null;
        prices?: [
            {
                days: 'weekday' | 'weekend';
                price: number;
            }
        ];
    };
}

export interface TrendStats {
  current: number;
  previous: number;
  change: number;
  percentageChange: number;
  trend: 'neutral' | 'up' | 'down'; // Assuming trend can be these values
}

export interface ServiceUtilization {
  groupId: string;
  groupName: string;
  serviceType: string;
  utilization: TrendStats;
  bookedHours: number;
  bookableHours: number;
  revenueCaptured: number;
  revenuePotential: number;
  currency: string;
}

/**
 * A room contracted out under a space agreement (#2144, ADR-0016 Amendment 5).
 *
 * These rooms are NOT in `perService`: they are occupied by contract rather
 * than sold on demand, so they leave the capture rate on both sides. They are
 * reported here instead, because capacity and revenue that vanish from a KPI
 * without a trace are how a wrong number survives.
 */
export interface ContractedRoom {
  groupId: string;
  groupName: string;
  serviceType: string;
  /** Bookable hours in the period that left the on-demand denominator. */
  contractedHours: number;
  revenueCaptured: number;
  currency: string;
  /** False when the agreement started inside this period. */
  wasContracted: boolean;
}

export interface UtilizationStats {
  revenueCaptureRate: TrendStats;
  perService: ServiceUtilization[];
  /**
   * Optional: absent from a deployment that predates #2144, which must read
   * as "nothing contracted", never as "no data".
   */
  contracted?: ContractedRoom[];
  contractedHours?: TrendStats;
}

// Per-group entries for the bookings / revenues KPIs (#895). Same shape
// regardless of which metric they belong to: identifying triplet plus
// the standard TrendStats for that group's count / amount.
export interface ServiceBookingMetric {
  groupId: string;
  groupName: string;
  serviceType: string;
  current: TrendStats;
}

export interface ServiceRevenueMetric {
  groupId: string;
  groupName: string;
  serviceType: string;
  /** Net for this group — `current.current` is gross − refunds − disputes. */
  current: TrendStats;
  // #2028: the three figures the net is made of, current period only. Optional
  // to tolerate the deploy gap before the backend slice ships; after that they
  // are always present.
  gross?: number;
  refunds?: number;
  disputes?: number;
}

export interface StatsData {
  // `perService` is optional only to tolerate the brief deploy gap before
  // the backend slice of #895 ships. After it's deployed, the frontend
  // can rely on it being present for the service-selector aggregation.
  //
  // `bookings` / `revenues` / `utilization` are absent when that metric's query
  // failed on the backend, which then names it in `failedMetrics` (#2864) —
  // a failed metric is reported as failed, never as zero.
  bookings?: TrendStats & { perService?: ServiceBookingMetric[] };
  // #2028: `revenues.current` is NET — gross minus refunds and dispute losses.
  // `gross` / `refunds` / `disputes` carry the three figures separately, never
  // blended, because "took 100, gave back 40" and "took 60" are different facts.
  revenues?: TrendStats & {
    perService?: ServiceRevenueMetric[];
    gross?: number;
    refunds?: number;
    disputes?: number;
  };
  // Optional to tolerate a brief deploy gap where the backend hasn't shipped #877 yet.
  utilization?: UtilizationStats;
  failedMetrics?: DashboardMetric[];
}

export type DashboardMetric = 'bookings' | 'revenues' | 'utilization';

// Dashboard "Latest activity" cards (#878). Period-agnostic — the from/to
// path params on /dashboard filter the stats block only; recent activity is
// always the 5 most-recent items overall.
export interface RecentBookingItem {
  id: string;
  bookingNumber: string;
  useCase: string;
  customerName: string | null;
  assetName: string | null;
  status: string; // canonical booking status enum (e.g. 'booked', 'paid')
  createdAt: string; // ISO 8601
}

export interface RecentIssueReportItem {
  id: string;
  state: string; // damage_report_state_enum (e.g. 'open', 'in_progress')
  bookingNumber: string | null;
  issueType: string | null;
  assetName: string | null;
  createdAt: string; // ISO 8601
}

export interface DashboardStatsResponse {
  stats: StatsData;
  recentBookings: RecentBookingItem[];
  recentIssueReports: RecentIssueReportItem[];
  /**
   * M8 approval (#1809): room reservations awaiting approval right now —
   * not limited to the selected period, an open decision is open whenever it
   * came in. Absent on an older backend, and 0 on a legacy tenant.
   */
  awaitingApproval?: number;
}
